import React, { useMemo } from "react";
import { Card, CardContent, CardMedia, Typography, Box, Divider, IconButton } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';

const CARD_SX = {
    display: 'flex',
    borderRadius: 3,
    boxShadow: 3,
    overflow: 'hidden',
    position: 'relative',
    marginTop: '20px',
};

const CLOSE_BUTTON_SX = {
    position: 'absolute',
    top: 8,
    right: 8,
    color: 'grey.600',
    zIndex: 1,
};

const CARD_MEDIA_SX = {
    width: 250,
    objectFit: 'cover',
    marginTop: '3%',
    padding: '1%',
};

const CARD_CONTENT_SX = {
    p: 3,
    flex: 2,
};

const COLUMNS_CONTAINER_SX = {
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    gap: 3,
};

const PHYSICAL_DATA_BOX_SX = {
    flex: 1,
    borderRadius: 2,
    p: 2,
};

const TECHNICAL_ITEM_BOX_SX = {
    display: "flex",
    justifyContent: "space-between",
    mb: 1,
};

const formatValue = (valueObj) => {
    if (!valueObj) return "-";
    const value = valueObj.value ?? "-";
    const unit = valueObj.unit ?? "";
    return `${value} ${unit}`.trim();
};

const getTechnicalItems = (tc) => [
    {
        label: "Capacidade de desgaseificação",
        value: formatValue(tc?.capacity),
    },
    {
        label: "Capacidade do reservatório de armazenagem",
        value: formatValue(tc?.storage?.capacity),
    },
    {
        label: "Teor de O2 no tanque armazenagem",
        value: formatValue(tc?.storage?.oxygen),
    },
    {
        label: "Pressão de serviço",
        value: formatValue(tc?.workingConditions?.pressure),
    },
    {
        label: "Temperatura normal de serviço",
        value: formatValue(tc?.workingConditions?.temperature),
    },
    {
        label: "Caudal máximo de água de Make-up",
        value: formatValue(tc?.makeupWater?.flow),
    },
    {
        label: "Temperatura da água de Make-up",
        value: formatValue(tc?.makeupWater?.temperature),
    },
    {
        label: "Temperatura condensados",
        value: formatValue(tc?.condensate?.temperature),
    },
    {
        label: "Temperatura do vapor",
        value: formatValue(tc?.steam?.temperature),
    },
];

const CloseButton = React.memo(({ onClose }) => {
    if (!onClose) return null;

    return (
        <IconButton
            onClick={onClose}
            sx={CLOSE_BUTTON_SX}
            aria-label="Fechar detalhes"
        >
            <CloseIcon />
        </IconButton>
    );
});

CloseButton.displayName = "CloseButton";

const DeaeratorImage = React.memo(({ image, name }) => (
    <CardMedia
        component="img"
        sx={CARD_MEDIA_SX}
        image={image}
        alt={name}
    />
));

DeaeratorImage.displayName = "DeaeratorImage";

const DeaeratorHeader = React.memo(({ name, description }) => (
    <>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
            {name}
        </Typography>

        <Typography variant="body2" color="text.secondary" paragraph>
            {description}
        </Typography>

        <Divider sx={{ my: 2 }} />
    </>
));

DeaeratorHeader.displayName = "DeaeratorHeader";

const TechnicalItem = ({ label, value }) => (
    <Box sx={TECHNICAL_ITEM_BOX_SX}>
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="body2">{value}</Typography>
    </Box>
);

TechnicalItem.displayName = "TechnicalItem";

const TechnicalCharacteristics = React.memo(({ items }) => (
    <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Características Técnicas Principais
        </Typography>

        {items.map((item, index) => (
            <TechnicalItem
                key={index}
                label={item.label}
                value={item.value}
            />
        ))}
    </Box>
));

TechnicalCharacteristics.displayName = "TechnicalCharacteristics";

const PhysicalData = () => (
    <Box sx={PHYSICAL_DATA_BOX_SX}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Dados Físicos
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
            Aqui podes colocar dados adicionais, especificações, ou até uma imagem.
        </Typography>
    </Box>
);

PhysicalData.displayName = "PhysicalData";

export default function DeaeratorDetail({ deaerator, onClose }) {
    const technicalItems = useMemo(
        () => getTechnicalItems(deaerator?.technicalCharacteristics),
        [deaerator?.technicalCharacteristics]
    );

    if (!deaerator) {
        return (
            <Card sx={CARD_SX}>
                <CardContent>
                    <Typography variant="body1" color="text.secondary">
                        Nenhum desgasificador selecionado
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card sx={CARD_SX}>
            <CloseButton onClose={onClose} />

            <DeaeratorImage
                image={deaerator.image}
                name={deaerator.name}
            />

            <CardContent sx={CARD_CONTENT_SX}>
                <DeaeratorHeader
                    name={deaerator.name}
                    description={deaerator.description}
                />

                <Box sx={COLUMNS_CONTAINER_SX}>
                    <TechnicalCharacteristics items={technicalItems} />
                    <PhysicalData />
                </Box>
            </CardContent>
        </Card>
    );
}